import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserSessionInfo = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const sessions = [
    {
      id: 1,
      userId: 'USR001',
      userName: 'Sarah Mitchell',
      email: 'sarah.mitchell@businessanalytics.com',
      role: 'C-Level Executive',
      status: 'active',
      loginTime: '2026-02-10T01:15:00',
      lastActivity: '2026-02-10T02:30:00',
      ipAddress: '192.168.1.45',
      device: 'Windows Desktop',
      browser: 'Chrome 121.0',
      location: 'New York, NY',
      sessionDuration: 75,
    },
    {
      id: 2,
      userId: 'USR002',
      userName: 'Michael Chen',
      email: 'michael.chen@businessanalytics.com',
      role: 'Business Analyst',
      status: 'active',
      loginTime: '2026-02-10T00:45:00',
      lastActivity: '2026-02-10T02:28:00',
      ipAddress: '192.168.1.67',
      device: 'MacBook Pro',
      browser: 'Safari 17.2',
      location: 'San Francisco, CA',
      sessionDuration: 103,
    },
    {
      id: 3,
      userId: 'USR003',
      userName: 'Emily Rodriguez',
      email: 'emily.rodriguez@businessanalytics.com',
      role: 'Operations Manager',
      status: 'active',
      loginTime: '2026-02-10T02:00:00',
      lastActivity: '2026-02-10T02:32:00',
      ipAddress: '192.168.1.89',
      device: 'iPad Pro',
      browser: 'Safari Mobile 17.2',
      location: 'Chicago, IL',
      sessionDuration: 32,
    },
    {
      id: 4,
      userId: 'USR004',
      userName: 'David Thompson',
      email: 'david.thompson@businessanalytics.com',
      role: 'IT Administrator',
      status: 'idle',
      loginTime: '2026-02-09T23:30:00',
      lastActivity: '2026-02-10T01:45:00',
      ipAddress: '192.168.1.12',
      device: 'Windows Desktop',
      browser: 'Edge 121.0',
      location: 'Seattle, WA',
      sessionDuration: 195,
    },
    {
      id: 5,
      userId: 'USR005',
      userName: 'Jennifer Lee',
      email: 'jennifer.lee@businessanalytics.com',
      role: 'Business Analyst',
      status: 'active',
      loginTime: '2026-02-10T01:30:00',
      lastActivity: '2026-02-10T02:25:00',
      ipAddress: '192.168.1.34',
      device: 'MacBook Air',
      browser: 'Chrome 121.0',
      location: 'Boston, MA',
      sessionDuration: 65,
    },
    {
      id: 6,
      userId: 'USR006',
      userName: 'Robert Anderson',
      email: 'robert.anderson@businessanalytics.com',
      role: 'C-Level Executive',
      status: 'expired',
      loginTime: '2026-02-09T20:00:00',
      lastActivity: '2026-02-09T22:15:00',
      ipAddress: '192.168.1.56',
      device: 'Windows Desktop',
      browser: 'Firefox 122.0',
      location: 'Austin, TX',
      sessionDuration: 135,
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'idle':
        return 'text-warning bg-warning/10';
      case 'expired':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'Circle';
      case 'idle':
        return 'Clock';
      case 'expired':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const filteredSessions = sessions?.filter((session) => {
    const matchesSearch =
      session?.userName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      session?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      session?.userId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' || session?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const activeCount = sessions?.filter((s) => s?.status === 'active')?.length;
  const idleCount = sessions?.filter((s) => s?.status === 'idle')?.length;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Active Sessions
              </p>
              <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                {activeCount}
              </p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="Users" size={20} className="text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Idle Sessions
              </p>
              <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                {idleCount}
              </p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Total Sessions
              </p>
              <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                {sessions?.length}
              </p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-primary" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <div className="relative flex-1">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search by name, email, or user ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="idle">Idle</option>
            <option value="expired">Expired</option>
          </select>
          <Button variant="outline" iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        {filteredSessions?.map((session) => (
          <div
            key={session?.id}
            className="bg-card border border-border rounded-md p-4 md:p-6 hover:shadow-md transition-smooth"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 text-primary font-caption font-semibold flex-shrink-0">
                  {getInitials(session?.userName)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 md:gap-3 mb-1">
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                      {session?.userName}
                    </h3>
                    <div
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption ${getStatusColor(
                        session?.status
                      )}`}
                    >
                      <Icon name={getStatusIcon(session?.status)} size={12} />
                      <span className="capitalize">{session?.status}</span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-1">
                    {session?.email}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">
                    {session?.role} • User ID: {session?.userId}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-3 md:mt-4">
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Login Time
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {formatTimestamp(session?.loginTime)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Last Activity
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {formatTimestamp(session?.lastActivity)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Session Duration
                      </p>
                      <p className="text-sm font-medium text-foreground font-data">
                        {formatDuration(session?.sessionDuration)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Location
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {session?.location}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-caption">
                      <Icon name="Monitor" size={14} />
                      <span>{session?.device}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-caption">
                      <Icon name="Globe" size={14} />
                      <span>{session?.browser}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-caption font-data">
                      <Icon name="MapPin" size={14} />
                      <span>{session?.ipAddress}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex lg:flex-col gap-2">
                <Button variant="outline" size="sm" iconName="Eye">
                  View Details
                </Button>
                {session?.status === 'active' && (
                  <Button variant="destructive" size="sm" iconName="LogOut">
                    End Session
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredSessions?.length === 0 && (
        <div className="text-center py-12 md:py-16">
          <Icon
            name="Users"
            size={48}
            className="mx-auto mb-4 text-muted-foreground"
          />
          <p className="text-muted-foreground">No sessions found</p>
        </div>
      )}
    </div>
  );
};

export default UserSessionInfo;